import React, { useState, Fragment, useRef, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon, SearchIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { getTimeZones } from '@vvo/tzdb';

interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

export const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  label,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false); // Track open state
  const searchInputRef = useRef<HTMLInputElement>(null);
  const timezones = getTimeZones();

  const filteredTimezones =
    query === ''
      ? timezones
      : timezones.filter((timezone) => {
          const searchStr = `${timezone.name} ${
            timezone.alternativeName
          } ${timezone.mainCities.join(' ')}`.toLowerCase();
          return searchStr.includes(query.toLowerCase());
        });

  const formatTimezone = (timezone: ReturnType<typeof getTimeZones>[0]) => {
    const offsetMinutes = timezone.currentTimeOffsetInMinutes;
    const hours = Math.abs(Math.floor(offsetMinutes / 60));
    const minutes = Math.abs(offsetMinutes % 60);
    const sign = offsetMinutes >= 0 ? '+' : '-';

    return `(GMT${sign}${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}) ${timezone.alternativeName} - ${
      timezone.mainCities[0]
    }`;
  };

  const selectedTimezone = timezones.find((tz) => tz.name === value);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  return (
    <div className="relative w-full">
      <Listbox value={value} onChange={onChange}>
        {({ open }) => {
          if (open !== isOpen) setIsOpen(open); // Sync open state

          return (
            <>
              <div className="mb-1 block text-caption text-secondary-125">
                {label}
              </div>
              <div className="relative">
                <Listbox.Button className="relative w-full cursor-default rounded-md bg-neutral-50 py-4 pl-3 pr-10 text-left text-caption text-neutral-150 focus:outline-none focus:ring-2 focus:ring-secondary-50">
                  <span className="block truncate">
                    {selectedTimezone
                      ? formatTimezone(selectedTimezone)
                      : 'Select timezone'}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-neutral-125"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>

                <Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                  afterEnter={() => {
                    searchInputRef.current?.focus();
                    searchInputRef.current?.select();
                  }}
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-caption">
                    <div className="sticky top-0 z-10 border-b border-neutral-50 bg-white">
                      <div className="flex items-center px-3 py-2">
                        <SearchIcon
                          className="h-4 w-4 text-neutral-125"
                          aria-hidden="true"
                        />
                        <input
                          ref={searchInputRef}
                          type="text"
                          className="w-full border-0 bg-white p-1 pl-2 text-caption text-neutral-150 placeholder:text-neutral-125 focus:outline-none focus:ring-0"
                          placeholder="Search timezones..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>

                    {filteredTimezones.length === 0 && query !== '' ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-neutral-125">
                        Nothing found.
                      </div>
                    ) : (
                      filteredTimezones.map((timezone) => (
                        <Listbox.Option
                          key={timezone.name}
                          className={({ active }) =>
                            classNames(
                              'relative cursor-default select-none whitespace-nowrap py-2 pl-3 pr-9',
                              active ? 'bg-secondary-20' : '',
                            )
                          }
                          value={timezone.name}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={classNames(
                                  'block truncate',
                                  selected ? 'font-semibold' : '',
                                )}
                              >
                                {formatTimezone(timezone)}
                              </span>
                            </>
                          )}
                        </Listbox.Option>
                      ))
                    )}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          );
        }}
      </Listbox>
    </div>
  );
};
